import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import {
  ApiError,
  getMerchantSettings,
  updateMerchantSettings,
  type MerchantSettings,
} from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/DataTable";
import { Save, CheckCircle2, Lock, ChevronRight } from "lucide-react";

export function MerchantSettingsPage() {
  const [settings, setSettings] = useState<MerchantSettings | null>(null);
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const s = await getMerchantSettings();
      setSettings(s);
      setPhone(s.business_phone ?? "");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const flash = (msg: string) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 3000);
  };

  const onSave = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await updateMerchantSettings({ business_phone: phone || null });
      flash("Paramètres enregistrés");
      await load();
    } catch (err) {
      if (err instanceof ApiError) setError(err.detail || err.title);
      else setError("Erreur");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) return <div className="card card-body text-[#7e8299]">Chargement…</div>;

  return (
    <div>
      <PageHeader title="Paramètres" breadcrumbs={[{ label: "Mon espace" }, { label: "Paramètres" }]} />

      {success && (
        <div className="rounded-lg bg-success-50 text-success-700 px-4 py-3 mb-4 inline-flex items-center gap-2">
          <CheckCircle2 size={16} />
          {success}
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div className="card-title">Informations marchand</div>
        </div>
        <form onSubmit={onSave}>
          <div className="card-body space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Readonly label="Nom commercial" value={settings.business_name} />
              <Readonly label="Email business" value={settings.business_email} />
              <Readonly label="Pays" value={settings.country} />
              <Readonly
                label="Mode"
                value={<Badge color={settings.livemode ? "info" : "light"}>{settings.livemode ? "Livemode" : "Test"}</Badge>}
              />
            </div>

            <div className="border-t border-[#eff2f5] pt-5">
              <div className="text-xs uppercase tracking-wider text-[#7e8299] font-bold mb-4">Modifiable</div>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="form-label">Téléphone business</label>
                  <input value={phone} onChange={(e) => setPhone(e.target.value)} className="form-input" placeholder="+243..." maxLength={32} />
                </div>
              </div>
            </div>

            {error && <div className="bg-danger-50 text-danger-700 text-sm rounded-lg px-4 py-3">{error}</div>}
          </div>
          <div className="card-footer">
            <button type="submit" disabled={saving} className="btn btn-primary btn-sm ml-auto">
              <Save size={14} />
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </form>
      </div>

      {/* Section Sécurité */}
      <div className="card mt-5">
        <div className="card-header">
          <div className="card-title">Sécurité</div>
        </div>
        <div className="card-body">
          <Link
            to="/change-password"
            className="flex items-center justify-between p-3 rounded-lg border border-[#eff2f5] hover:bg-[#fcfcfd] transition-colors group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center">
                <Lock size={18} />
              </div>
              <div>
                <div className="font-semibold text-[#181c32] text-sm group-hover:text-brand-700 transition-colors">
                  Changer le mot de passe
                </div>
                <div className="text-xs text-[#7e8299]">Mettez à jour votre mot de passe personnel.</div>
              </div>
            </div>
            <ChevronRight size={16} className="text-[#b5b5c3] group-hover:text-brand-700 group-hover:translate-x-0.5 transition-all" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function Readonly({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-[#7e8299] font-bold mb-1.5">{label}</div>
      <div className="text-sm text-[#181c32]">{value}</div>
    </div>
  );
}
